import { h } from 'preact'; /** @jsx h */
import { useEffect, useState } from 'preact/hooks';
import { fetchProducts } from '../lib/fetchProducts';
import useWindowSize from '../utils/windowSize';

import Breadcrumbs from '../components/Breadcrumbs/Breadcrumbs';
import Hero from '../components/Hero/Hero';
import LoadMore from '../components/LoadMore/LoadMore';
import ProductItem from '../components/ProductItem/ProductItem';
import ProductList from '../components/ProductList/ProductList';
import Sidebar from '../components/Sidebar/Sidebar';

import '../styles/productsPage.scss';

const ProductsPage = ({ slug = null }) => {
    // Get the width of the viewport
    const { width } = useWindowSize();

    // Set products per page based on viewport width
    let productsPerPage = 8;
    if (width && width >= 1440) productsPerPage = 9;

    // States
    const [products, setProducts] = useState([]);
    const [featuredProduct, setFeaturedProduct] = useState();
    const [categories, setCategories] = useState(new Set());
    const [isHeaderUp, setIsHeaderUp] = useState(false);
    const [shownProducts, setShownProducts] = useState(productsPerPage);
    const [categoryFilters, setCategoryFilters] = useState(new Set());
    const [sortFilter, setSortFilter] = useState('new');
    const [priceFilter, setPriceFilter] = useState('0');

    // Set shown products to productsPerPage when productsPerPage changes
    useEffect(() => {
        setShownProducts(productsPerPage);
    }, [productsPerPage]);

    // Populate categoryFilters with capitalized slug if slug is not null
    useEffect(() => {
        if (slug) {
            // Replace hyphens with spaces and capitalize first letter if the first word in the string
            const category = slug.replace(/-/g, ' ').replace(/^\w/, l => l.toUpperCase());

            // Set category filters to a new Set with the slug as the only element
            setCategoryFilters(new Set([category]));
        }
    }, [slug]);

    // Push Category View Data to Matomo - Fill category dynamically based on slug
    useEffect(() => {
        if (typeof window !== 'undefined' && window._paq) {
            if (slug) {
                // Replace hyphens with spaces and capitalize first letter if the first word in the string
                const category = slug.replace(/-/g, ' ').replace(/^\w/, l => l.toUpperCase());

                // Push category view data to Matomo
                window._paq.push(['setEcommerceView', false, false, category]);
            } else {
                // Push category view data to Matomo
                window._paq.push(['setEcommerceView', false, false, 'Alle produkter']);
            }
        }
    }, [slug]);

    // Fetch products from API
    useEffect(() => {
        const getProducts = async () => {
            const response = await fetchProducts([], [1, 2, 3]); // Fetch all products from categories 1, 2 and 3. Not from 4 (Ekstra gaver)
            const products = response.filter(product => product.active === true);

            // Set products
            setProducts(products);

            // Set featured product
            const featuredProducts = products.filter(product => product.featured === true);
            setFeaturedProduct(featuredProducts[0] || null);

            // Sort categories alphabetically and save to state
            const categories = new Set(products.map(product => product.catName).sort());
            setCategories(categories);
        };
        getProducts();
    }, []);

    // Add event listener to header to check if header is up
    useEffect(() => {
        const onScroll = () => {
            const header = document.querySelector('header');
            const headerClassList = header.classList;
            const isHeaderUp = headerClassList.contains('header-up');
            setIsHeaderUp(isHeaderUp);
        };
        window.addEventListener('scroll', onScroll);
    }, []);

    // Add event listener to price range slider
    useEffect(() => {
        const range = document.querySelector('.ws-filter-price-range-slider-range');
        const value = document.querySelector('.ws-filter-price-range-slider-values');

        if (range && value) {
            value.innerHTML = 0;
            range.addEventListener('input', e => {
                value.innerHTML = range.value;
            });
        }
    }, []);

    // Load more products
    const handleLoadMore = e => {
        setShownProducts(shownProducts + productsPerPage);
        e.currentTarget.blur();
    };

    // Update category filters
    const updateFilters = (checked, categoryFilter) => {
        if (checked) setCategoryFilters(prev => new Set(prev).add(categoryFilter));
        if (!checked)
            setCategoryFilters(prev => {
                const next = new Set(prev);
                next.delete(categoryFilter);
                return next;
            });
    };

    // Filter products based on category
    const filteredProducts =
        categoryFilters.size === 0
            ? products.filter(p => parseFloat(p.price) >= priceFilter)
            : products
                  .filter(p => categoryFilters.has(p.catName))
                  .filter(p => parseFloat(p.price) >= priceFilter);

    // Sort options
    const sortOptions = [
        { key: 'new', value: 'Nyeste' },
        { key: 'name-desc', value: 'Alfabetisk A-Å' },
        { key: 'price-desc', value: 'Pris høy lav' },
        { key: 'price-asc', value: 'Pris lav høy' }
    ];

    // Sort products based on sort option
    const sortProducts = (products, sortByValue) => {
        switch (sortByValue) {
            case 'new':
                return products.sort((a, b) => {
                    // Treat sort values of 0 as lowest sorted
                    const aSort = parseFloat(a.sort) || Number.MAX_SAFE_INTEGER;
                    const bSort = parseFloat(b.sort) || Number.MAX_SAFE_INTEGER;

                    return aSort - bSort;
                });
            case 'name-desc':
                return products.sort((a, b) => a.name.localeCompare(b.name));
            case 'price-desc':
                return products.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
            case 'price-asc':
                return products.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
            default:
                return products;
        }
    };

    // Sort filtered products
    sortProducts(filteredProducts, sortFilter);

    return (
        <div className="ws-main">
            <Hero />
            <Breadcrumbs slug={slug} />
            <div className="ws-shop-area">
                <Sidebar
                    isHeaderUp={isHeaderUp}
                    products={products}
                    categories={[...categories]}
                    categoryFilters={categoryFilters}
                    setCategoryFilters={updateFilters}
                    sortOptions={sortOptions}
                    sortFilter={sortFilter}
                    setSortFilter={setSortFilter}
                    priceFilter={priceFilter}
                    setPriceFilter={setPriceFilter}
                    resultCount={filteredProducts.length}
                />
                <div className="ws-shop-area-products">
                    {featuredProduct && (
                        <div className="ws-products-featured">
                            <ProductItem product={featuredProduct} isFeatured />
                        </div>
                    )}
                    <ProductList
                        title="Gaver"
                        emptyMessage="Ingen produkter funnet..."
                        products={filteredProducts}
                        shownProductsCount={shownProducts}
                    />
                    <LoadMore
                        shownProductsCount={shownProducts}
                        totalProductsCount={filteredProducts.length}
                        handler={handleLoadMore}
                        buttonText="Last inn flere"
                    />
                </div>
            </div>
        </div>
    );
};

export default ProductsPage;
